import { useContext } from 'react';
import type { ChangeSetCommentThread } from '@erp/rpc';
import { AuthContext } from 'src/context/auth.context';
import {
  useChangeSetMentionableUsers,
  useDeleteChangeSetCommentMessage,
  useEditChangeSetCommentMessage,
  useReopenChangeSetCommentThread,
  useReplyToChangeSetCommentThread,
  useResolveChangeSetCommentThread,
} from '@/api/rpc/hooks/inventory-management/useChangeSetComments';
import { CommentThread } from './CommentThread';

/**
 * A {@link CommentThread} wired to the review's comment mutations and the
 * signed-in user — what the grid popover and the comments panel render. Every
 * write refreshes the review's thread lists.
 */
export function ReviewCommentThread({
  changeSetId,
  thread,
  className,
}: {
  changeSetId: string;
  thread: ChangeSetCommentThread;
  className?: string;
}) {
  const { currentUser } = useContext(AuthContext);
  const reply = useReplyToChangeSetCommentThread(changeSetId);
  const edit = useEditChangeSetCommentMessage(changeSetId);
  const remove = useDeleteChangeSetCommentMessage(changeSetId);
  const resolve = useResolveChangeSetCommentThread(changeSetId);
  const reopen = useReopenChangeSetCommentThread(changeSetId);
  const mentionable = useChangeSetMentionableUsers(changeSetId);
  const threadId = thread.id;

  return (
    <CommentThread
      thread={thread}
      currentUserId={currentUser?.id}
      className={className}
      isReplying={reply.isPending}
      mentionableUsers={mentionable.data}
      onReply={async (body) => {
        await reply.mutateAsync({ threadId, body });
      }}
      onEditMessage={async (messageId, body) => {
        await edit.mutateAsync({ messageId, body });
      }}
      onDeleteMessage={async (messageId) => {
        await remove.mutateAsync({ messageId });
      }}
      onResolve={async () => {
        await resolve.mutateAsync({ threadId });
      }}
      onReopen={async () => {
        await reopen.mutateAsync({ threadId });
      }}
    />
  );
}
